import { oc } from "@orpc/contract";
import { z } from "zod";
import { TroubleDataSchema } from "@erp/shared-types";

/**
 * Scalar helpers
 */
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

export const APInvoiceGLAccountSummarySchema = z.object({
  id: UUIDSchema,
  accountCode: z.string(),
  accountName: z.string(),
  accountDescription: z.string().nullable().optional(),
  accountType: z.string().nullable().optional(),
  isControlAccount: z.boolean(),
});

export const APInvoiceSupplierGLAccountsSchema = z.object({
  expense: APInvoiceGLAccountSummarySchema.nullable().optional(),
  ap: APInvoiceGLAccountSummarySchema.nullable().optional(),
  po: APInvoiceGLAccountSummarySchema.nullable().optional(),
});

export const APInvoiceSupplierSummarySchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  contactName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  paymentTerms: z.string().nullable().optional(),
  status: z.string(),
  notes: z.array(z.string()).nullable().optional(),
  defaultPaymentMethod: z.string().nullable().optional(),
  glAccounts: APInvoiceSupplierGLAccountsSchema.nullable().optional(),
});

/**
 * Pagination schema mirroring common backend pattern
 */
export const APInvoicePaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Filters accepted by the list endpoint
 * All fields optional; server treats undefined as no filter
 */
export const ListAPInvoicesInputSchema = z.object({
  invoiceDateFrom: z.string().optional(),
  invoiceDateTo: z.string().optional(),
  dueDateFrom: z.string().optional(),
  dueDateTo: z.string().optional(),
  status: z.array(z.string()).optional(),
  paymentStatus: z.array(z.string()).optional(),
  supplierId: UUIDSchema.optional(),
  invoiceNumber: z.string().optional(),
  referenceId: z.string().optional(),
  minTotalAmount: DecimalStringSchema.optional(),
  maxTotalAmount: DecimalStringSchema.optional(),
  sortBy: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
  includeNumLineItemsMatched: z.boolean().optional(),
});

/**
 * Base invoice schema mirroring Sequelize attributes (sans line items)
 */
export const APInvoiceSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  supplierId: UUIDSchema.nullable().optional(),
  invoiceNumber: z.string().nullable().optional(),
  /** @deprecated Use referenceIds instead */
  referenceId: z.string().nullable().optional(),
  referenceIds: z.array(z.string()).nullable().optional(),
  invoiceDate: z.string(),
  receivedAt: z.string().nullable().optional(),
  dueDate: z.string(),
  subtotal: DecimalStringSchema,
  taxAmount: DecimalStringSchema.nullable().optional(),
  totalAmount: DecimalStringSchema,
  currency: z.string(),
  paymentTerms: z.string().nullable().optional(),
  paymentStatus: z.string(),
  paidAt: z.string().nullable().optional(),
  paidAmount: DecimalStringSchema.nullable().optional(),
  preferredPayDate: z.string().nullable().optional(),
  scheduledPaymentDate: z.string().nullable().optional(),
  scheduledPaymentExplanation: z.string().nullable().optional(),
  approvedAt: z.string().nullable().optional(),
  approvedByUserId: UUIDSchema.nullable().optional(),
  isVoided: z.boolean(),
  voidedAt: z.string().nullable().optional(),
  voidedByUserId: UUIDSchema.nullable().optional(),
  voidReason: z.string().nullable().optional(),
  paymentHold: z.boolean(),
  paymentHoldReason: z.string().nullable().optional(),
  paymentHoldByUserId: UUIDSchema.nullable().optional(),
  paymentHoldAt: z.string().nullable().optional(),
  // Non-status "flagged for a reason" indicators (e.g. matching paused while a
  // matched order isn't yet in a status that allows matching). Orthogonal to
  // `status`.
  troubleData: TroubleDataSchema,
  status: z.string(),
  source: z.string().nullable().optional(),
  sourceId: UUIDSchema.nullable().optional(),
  glAccountId: UUIDSchema.nullable().optional(),
  costCenterId: UUIDSchema.nullable().optional(),
  departmentId: UUIDSchema.nullable().optional(),
  notes: z.string().nullable().optional(),
  internalNotes: z.string().nullable().optional(),
  originId: z.string().nullable().optional(),
  originSystem: z.string().nullable().optional(),
  originEntity: z.string().nullable().optional(),
  originSyncStatus: z.string().nullable().optional(),
  originSyncErrorMessage: z.string().nullable().optional(),
  originMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  supplier: APInvoiceSupplierSummarySchema.nullable().optional(),
  glAccount: APInvoiceGLAccountSummarySchema.nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  lineItemMatchSummary: z
    .object({
      matched: z.int().nonnegative(),
      confirmed: z.int().nonnegative(),
      tentative: z.int().nonnegative(),
      unmatched: z.int().nonnegative(),
      total: z.int().nonnegative(),
    })
    .nullable()
    .optional(),
});

export const ListAPInvoicesResponseSchema = z.object({
  invoices: z.array(APInvoiceSchema),
  pagination: APInvoicePaginationSchema,
});

export const GetAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const GetAPInvoiceExtractionResultInputSchema = z.object({
  id: UUIDSchema,
});

export const APInvoiceExtractionResultSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  apInvoiceId: UUIDSchema.nullable(),
  commInboundEmailId: UUIDSchema.nullable(),
  documentId: UUIDSchema.nullable(),
  modelUsed: z.string(),
  status: z.enum(["success", "failed"]),
  extractedData: z.unknown().nullable(),
  rawOutput: z.unknown().nullable(),
  errorDetails: z.unknown().nullable(),
  isSelected: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const GetAPInvoiceExtractionResultResponseSchema = z.object({
  extractionResult: APInvoiceExtractionResultSchema.nullable(),
});

export const APInvoiceLineItemSchema = z.object({
  id: UUIDSchema,
  lineNumber: z.int(),
  description: z.string().nullable().optional(),
  quantity: z.union([z.number(), z.string()]).nullable().optional(),
  unitPrice: DecimalStringSchema.nullable().optional(),
  totalPrice: DecimalStringSchema.nullable().optional(),
});

export const InboundEmailAttachmentSchema = z.object({
  id: UUIDSchema,
  fileName: z.string(),
  sizeBytes: z.number().nullable().optional(),
  mimeType: z.string().nullable().optional(),
  s3Url: z.string(),
  contentId: z.string().nullable().optional(),
});

export const InboundEmailSchema = z.object({
  id: UUIDSchema,
  conversationId: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  from: z.string().nullable(),
  to: z.string(),
  cc: z.string().nullable().optional(),
  htmlContent: z.string().nullable().optional(),
  textContent: z.string().nullable().optional(),
  attachments: z.array(InboundEmailAttachmentSchema).optional(),
  receivedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
});

/**
 * Summary of an email thread that contributed to an AP invoice.
 *
 * An invoice can be touched by multiple email threads over its lifetime:
 *  - the thread it was originally created from (`isSource: true`)
 *  - any later threads whose extraction triggered an update via
 *    `arbitrateAPInvoiceUpdate`
 *
 * Threads are derived by walking `ai_ap_invoice_extraction_results.apInvoiceId`
 * back to the originating `commInboundEmailId` (or `documentId` →
 * `DocumentAssociation` → inbound email).
 */
export const APInvoiceRelatedConversationSchema = z.object({
  conversationId: z.string(),
  primaryEmailId: UUIDSchema,
  subject: z.string().nullable(),
  isSource: z.boolean(),
});

export const GetAPInvoiceResponseSchema = z.object({
  invoice: APInvoiceSchema.nullable(),
  lineItems: z.array(APInvoiceLineItemSchema),
  inboundEmail: InboundEmailSchema.nullable(),
  relatedConversations: z.array(APInvoiceRelatedConversationSchema),
});

/**
 * Discriminates the kind of timeline event in the invoice Change History:
 * - `field_change`: a row-level INSERT/UPDATE/DELETE on the invoice or a line
 *   item (the original change-log behavior).
 * - `match`: a line item was matched/unmatched (a `core_entity_associations`
 *   row), labeled `manual` (user via API) or `auto` (workflow / AI).
 * - `workflow_run`: a workflow run (ingestion, on-approve, rerun) that
 *   processed this invoice.
 * - `scheduled_run`: a pending/snoozed rerun the invoice will execute next.
 */
export const InvoiceChangeLogKindSchema = z.enum([
  "field_change",
  "match",
  "workflow_run",
  "scheduled_run",
]);

export const InvoiceChangeLogEntrySchema = z.object({
  id: UUIDSchema,
  kind: InvoiceChangeLogKindSchema.default("field_change"),
  /**
   * Source table for `field_change`/`match` rows. `workflow_run` and
   * `scheduled_run` are synthetic (the id/entityId is the workflow run's id).
   */
  entityType: z.enum([
    "ap_invoices",
    "ap_invoice_line_items",
    "core_entity_associations",
    "workflow_run",
  ]),
  entityId: UUIDSchema,
  action: z.enum(["INSERT", "UPDATE", "DELETE"]),
  changedFields: z.record(z.string(), z.unknown()),
  actor: z.object({
    userId: UUIDSchema.nullable(),
    userName: z.string().nullable(),
    sourceType: z.enum(["api", "workflow", "system"]).nullable(),
    sourceDetail: z.string().nullable(),
  }),
  createdAt: z.coerce.date(),

  // --- `match` fields ---
  /** Whether the match was created manually (by a user) or automatically. */
  matchSource: z.enum(["manual", "auto"]).optional(),
  /** The entity the line item was matched to (PO line, charge, document, …). */
  matchTargetType: z.string().optional(),
  matchTargetId: z.string().optional(),

  // --- `workflow_run` / `scheduled_run` fields ---
  workflowRunId: UUIDSchema.optional(),
  /** Lineage id, needed to deep-link to the run-detail route. */
  workflowId: UUIDSchema.optional(),
  workflowName: z.string().nullable().optional(),
  runStatus: z.string().optional(),
  /** ISO timestamp of the next scheduled (snoozed) rerun. */
  scheduledFor: z.coerce.date().optional(),
  snoozeReason: z.string().nullable().optional(),
});

export const ListAPInvoiceChangeLogInputSchema = z.object({
  apInvoiceId: UUIDSchema,
});

export const ListAPInvoiceChangeLogResponseSchema = z.object({
  entries: z.array(InvoiceChangeLogEntrySchema),
});

export const CreateVoucherInFamousInputSchema = z.object({
  apInvoiceId: UUIDSchema,
});

export const CreateVoucherInFamousResponseSchema = z.object({
  success: z.boolean(),
  originId: z.string().optional(),
  errorMessage: z.string().optional(),
});

export const UpdateAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
  supplierId: UUIDSchema.nullable().optional(),
  /** @deprecated Use referenceIds instead */
  referenceId: z
    .string()
    .min(1, "Reference ID cannot be empty")
    .nullable()
    .optional(),
  referenceIds: z.array(z.string().min(1)).nullable().optional(),
  invoiceDate: z.string().optional(),
  dueDate: z.string().nullable().optional(),
  preferredPayDate: z.string().nullable().optional(),
  scheduledPaymentDate: z.string().nullable().optional(),
  scheduledPaymentExplanation: z.string().nullable().optional(),
});

export const UpdateAPInvoiceResponseSchema = APInvoiceSchema;

export const UpdateAPInvoiceLineItemInputSchema = z.object({
  id: UUIDSchema,
  apInvoiceId: UUIDSchema,
  quantity: DecimalStringSchema.optional(),
  unitPrice: DecimalStringSchema.optional(),
});

export const UpdateAPInvoiceLineItemResponseSchema = APInvoiceLineItemSchema;

export const AcceptMatchesAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const AcceptMatchesAPInvoiceResponseSchema = APInvoiceSchema;

export const UnacceptMatchesAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const UnacceptMatchesAPInvoiceResponseSchema = APInvoiceSchema;

export const CreatePaymentAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const CreatePaymentAPInvoiceResponseSchema = APInvoiceSchema;

export const BulkCreatePaymentsInputSchema = z.object({
  invoiceIds: z.array(UUIDSchema).min(1),
  scheduledDates: z.record(z.string(), z.string()).optional(),
});

export const BulkCreatePaymentsResponseSchema = z.object({
  paymentsCreated: z.number(),
  invoicesProcessed: z.number(),
  failures: z.array(
    z.object({
      invoiceId: z.string(),
      error: z.string(),
    }),
  ),
});

export const DeleteAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteAPInvoiceResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  deletedCounts: z
    .object({
      lineItems: z.int().nonnegative(),
      entityAssociations: z.int().nonnegative(),
      documentAssociations: z.int().nonnegative(),
      charges: z.int().nonnegative(),
      extractionResultsUnlinked: z.int().nonnegative(),
    })
    .optional(),
});

/**
 * Per-type structured summaries for supporting documents (AP-invoice-side
 * evidence: BOL, POD, Receiving Stamp, Carrier Confirmation). Discriminated on
 * `classificationType` so the frontend gets type-safe access to the fields
 * relevant for each doc type.
 *
 * The server fills missing fields with `null` rather than omitting them so
 * each summary has a stable shape per type. Free-form text fields are
 * sanitized server-side before being included.
 */
export const BillOfLadingSummarySchema = z.object({
  classificationType: z.literal("bill_of_lading"),
  bolNumber: z.string().nullable(),
  proNumber: z.string().nullable(),
  carrierName: z.string().nullable(),
  carrierScac: z.string().nullable(),
  shipDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  freightTerms: z.string().nullable(),
  poNumbers: z.array(z.string()),
  soNumbers: z.array(z.string()),
  totalPieces: z.number().nullable(),
  totalPallets: z.number().nullable(),
  totalWeight: z.number().nullable(),
  totalWeightUom: z.string().nullable(),
  lineItemCount: z.int().nonnegative(),
});

export const ProofOfDeliverySummarySchema = z.object({
  classificationType: z.literal("proof_of_delivery"),
  bolNumber: z.string().nullable(),
  proNumber: z.string().nullable(),
  carrierName: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  receivedByName: z.string().nullable(),
  signaturePresent: z.boolean().nullable(),
  poNumbers: z.array(z.string()),
  soNumbers: z.array(z.string()),
  lineItemCount: z.int().nonnegative(),
  exceptionCount: z.int().nonnegative(),
});

export const ReceivingStampSummarySchema = z.object({
  classificationType: z.literal("receiving_stamp"),
  underlyingDocumentType: z.string().nullable(),
  poNumber: z.string().nullable(),
  additionalPoNumbers: z.array(z.string()),
  bolNumber: z.string().nullable(),
  packingSlipNumber: z.string().nullable(),
  supplierName: z.string().nullable(),
  receivedDate: z.string().nullable(),
  receivedBy: z.string().nullable(),
  lineItemCount: z.int().nonnegative(),
  exceptionCount: z.int().nonnegative(),
});

export const CarrierConfirmationSummarySchema = z.object({
  classificationType: z.literal("carrier_confirmation"),
  confirmationNumber: z.string().nullable(),
  bolNumber: z.string().nullable(),
  proNumber: z.string().nullable(),
  carrierName: z.string().nullable(),
  carrierScac: z.string().nullable(),
  carrierMcNumber: z.string().nullable(),
  brokerName: z.string().nullable(),
  equipmentType: z.string().nullable(),
  agreedRate: z.number().nullable(),
  rateCurrency: z.string().nullable(),
  shipDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  poNumbers: z.array(z.string()),
  soNumbers: z.array(z.string()),
  rateBreakdownItemCount: z.int().nonnegative(),
});

export const ReceiptSummarySchema = z.object({
  classificationType: z.literal("receipt"),
  vendorName: z.string().nullable(),
  receiptNumber: z.string().nullable(),
  receiptDate: z.string().nullable(),
  totalAmount: z.number().nullable(),
  currency: z.string().nullable(),
  poNumbers: z.array(z.string()),
  invoiceNumbers: z.array(z.string()),
  referenceNumbers: z.array(z.string()),
  lineItemCount: z.int().nonnegative(),
});

export const SupportingDocSummarySchema = z.discriminatedUnion(
  "classificationType",
  [
    BillOfLadingSummarySchema,
    ProofOfDeliverySummarySchema,
    ReceivingStampSummarySchema,
    CarrierConfirmationSummarySchema,
    ReceiptSummarySchema,
  ],
);

export const SupportingDocClassificationSchema = z.enum([
  "bill_of_lading",
  "proof_of_delivery",
  "receiving_stamp",
  "carrier_confirmation",
  "receipt",
]);

export const SupportingDocOcrSchema = z.object({
  id: UUIDSchema,
  status: z.enum(["pending", "processing", "completed", "failed"]),
  vendor: z.string(),
  modelVersion: z.string(),
  error: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export const SupportingDocItemSchema = z.object({
  documentId: UUIDSchema,
  classificationType: SupportingDocClassificationSchema,
  originalFileName: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.int().nonnegative().nullable(),
  classifiedAt: z.coerce.date().nullable(),
  classificationConfidence: z.number().nullable(),
  associatedAt: z.coerce.date(),
  /**
   * Latest OCR result metadata. `null` if no OCR has been attempted for the
   * document yet. Use `core.documents.getOcrResultContent` with `id` to fetch
   * the full extracted JSON when the user expands the raw view.
   */
  ocr: SupportingDocOcrSchema.nullable(),
  /**
   * Server-computed structured digest of the OCR extraction. `null` when:
   *   - There is no OCR result yet, or
   *   - OCR is not in `completed` status, or
   *   - The extraction shape did not match the expected schema for this type
   *     (defensive — avoids crashing on partial / malformed extractions).
   */
  summary: SupportingDocSummarySchema.nullable(),
});

export const ListAPInvoiceSupportingDocumentsInputSchema = z.object({
  apInvoiceId: UUIDSchema,
});

export const ListAPInvoiceSupportingDocumentsResponseSchema = z.object({
  documents: z.array(SupportingDocItemSchema),
});

export const apInvoicesContracts = {
  list: oc
    .input(ListAPInvoicesInputSchema)
    .output(ListAPInvoicesResponseSchema)
    .route({
      method: "GET",
    }),
  get: oc
    .input(GetAPInvoiceInputSchema)
    .output(GetAPInvoiceResponseSchema)
    .route({
      method: "GET",
    }),
  getExtractionResult: oc
    .input(GetAPInvoiceExtractionResultInputSchema)
    .output(GetAPInvoiceExtractionResultResponseSchema)
    .route({
      method: "GET",
    }),
  update: oc
    .input(UpdateAPInvoiceInputSchema)
    .output(UpdateAPInvoiceResponseSchema)
    .route({
      method: "POST",
    }),
  createVoucherInFamous: oc
    .input(CreateVoucherInFamousInputSchema)
    .output(CreateVoucherInFamousResponseSchema)
    .route({
      method: "POST",
    }),
  acceptMatches: oc
    .input(AcceptMatchesAPInvoiceInputSchema)
    .output(AcceptMatchesAPInvoiceResponseSchema)
    .route({
      method: "POST",
    }),
  unacceptMatches: oc
    .input(UnacceptMatchesAPInvoiceInputSchema)
    .output(UnacceptMatchesAPInvoiceResponseSchema)
    .route({
      method: "POST",
    }),
  createPayment: oc
    .input(CreatePaymentAPInvoiceInputSchema)
    .output(CreatePaymentAPInvoiceResponseSchema)
    .route({
      method: "POST",
    }),
  bulkCreatePayments: oc
    .input(BulkCreatePaymentsInputSchema)
    .output(BulkCreatePaymentsResponseSchema)
    .route({
      method: "POST",
    }),
  delete: oc
    .input(DeleteAPInvoiceInputSchema)
    .output(DeleteAPInvoiceResponseSchema)
    .route({
      method: "POST",
    }),
  updateLineItem: oc
    .input(UpdateAPInvoiceLineItemInputSchema)
    .output(UpdateAPInvoiceLineItemResponseSchema)
    .route({
      method: "POST",
    }),
  listChangeLog: oc
    .input(ListAPInvoiceChangeLogInputSchema)
    .output(ListAPInvoiceChangeLogResponseSchema)
    .route({
      method: "GET",
    }),
  listSupportingDocuments: oc
    .input(ListAPInvoiceSupportingDocumentsInputSchema)
    .output(ListAPInvoiceSupportingDocumentsResponseSchema)
    .route({
      method: "GET",
    }),
} as const;

export type APInvoice = z.infer<typeof APInvoiceSchema>;
export type APInvoiceLineItem = z.infer<typeof APInvoiceLineItemSchema>;
export type InboundEmail = z.infer<typeof InboundEmailSchema>;
export type ListAPInvoicesInput = z.infer<typeof ListAPInvoicesInputSchema>;
export type ListAPInvoicesResponse = z.infer<
  typeof ListAPInvoicesResponseSchema
>;
export type GetAPInvoiceInput = z.infer<typeof GetAPInvoiceInputSchema>;
export type GetAPInvoiceResponse = z.infer<typeof GetAPInvoiceResponseSchema>;
export type APInvoiceRelatedConversation = z.infer<
  typeof APInvoiceRelatedConversationSchema
>;
export type GetAPInvoiceExtractionResultInput = z.infer<
  typeof GetAPInvoiceExtractionResultInputSchema
>;
export type APInvoiceExtractionResult = z.infer<
  typeof APInvoiceExtractionResultSchema
>;
export type GetAPInvoiceExtractionResultResponse = z.infer<
  typeof GetAPInvoiceExtractionResultResponseSchema
>;
export type CreateVoucherInFamousInput = z.infer<
  typeof CreateVoucherInFamousInputSchema
>;
export type CreateVoucherInFamousResponse = z.infer<
  typeof CreateVoucherInFamousResponseSchema
>;
export type UpdateAPInvoiceInput = z.infer<typeof UpdateAPInvoiceInputSchema>;
export type UpdateAPInvoiceResponse = z.infer<
  typeof UpdateAPInvoiceResponseSchema
>;
export type UpdateAPInvoiceLineItemInput = z.infer<
  typeof UpdateAPInvoiceLineItemInputSchema
>;
export type UpdateAPInvoiceLineItemResponse = z.infer<
  typeof UpdateAPInvoiceLineItemResponseSchema
>;
export type AcceptMatchesAPInvoiceInput = z.infer<
  typeof AcceptMatchesAPInvoiceInputSchema
>;
export type AcceptMatchesAPInvoiceResponse = z.infer<
  typeof AcceptMatchesAPInvoiceResponseSchema
>;
export type UnacceptMatchesAPInvoiceInput = z.infer<
  typeof UnacceptMatchesAPInvoiceInputSchema
>;
export type UnacceptMatchesAPInvoiceResponse = z.infer<
  typeof UnacceptMatchesAPInvoiceResponseSchema
>;
export type CreatePaymentAPInvoiceInput = z.infer<
  typeof CreatePaymentAPInvoiceInputSchema
>;
export type CreatePaymentAPInvoiceResponse = z.infer<
  typeof CreatePaymentAPInvoiceResponseSchema
>;
export type DeleteAPInvoiceInput = z.infer<typeof DeleteAPInvoiceInputSchema>;
export type DeleteAPInvoiceResponse = z.infer<
  typeof DeleteAPInvoiceResponseSchema
>;
export type InvoiceChangeLogEntry = z.infer<typeof InvoiceChangeLogEntrySchema>;
export type ListAPInvoiceChangeLogInput = z.infer<
  typeof ListAPInvoiceChangeLogInputSchema
>;
export type ListAPInvoiceChangeLogResponse = z.infer<
  typeof ListAPInvoiceChangeLogResponseSchema
>;

export type SupportingDocClassification = z.infer<
  typeof SupportingDocClassificationSchema
>;
export type BillOfLadingSummary = z.infer<typeof BillOfLadingSummarySchema>;
export type ProofOfDeliverySummary = z.infer<
  typeof ProofOfDeliverySummarySchema
>;
export type ReceivingStampSummary = z.infer<typeof ReceivingStampSummarySchema>;
export type CarrierConfirmationSummary = z.infer<
  typeof CarrierConfirmationSummarySchema
>;
export type ReceiptSummary = z.infer<typeof ReceiptSummarySchema>;
export type SupportingDocSummary = z.infer<typeof SupportingDocSummarySchema>;
export type SupportingDocOcr = z.infer<typeof SupportingDocOcrSchema>;
export type SupportingDocItem = z.infer<typeof SupportingDocItemSchema>;
export type ListAPInvoiceSupportingDocumentsInput = z.infer<
  typeof ListAPInvoiceSupportingDocumentsInputSchema
>;
export type ListAPInvoiceSupportingDocumentsResponse = z.infer<
  typeof ListAPInvoiceSupportingDocumentsResponseSchema
>;
